import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _get from 'lodash/get'
import { Map } from 'immutable'
import { connect } from 'react-redux'
import { useQuery } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import {
  VOTE_DIRECTION_DOWN,
  VOTE_DIRECTION_UP,
  VOTE_DIRECTION_LOVE,
  VOTE_DIRECTIONS,
  VOTE_SIZE_SMALL,
  VOTE_SIZE_MEDIUM,
  VOTE_SIZE_LARGE,
  recommend,
} from '../../services/recommend/index'
import { CONTENT_RATING_GA4_EVENT } from '../../services/event-tracking/index'
import { TYPE_CONTENT_VIDEO, TYPE_CONTENT_SERIES } from '../../services/content-type/index'
import { GET_USER_VOTE } from '../../services/recommend/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { getPrimary } from '../../services/languages/index'
import Button, { TYPES } from '../Button/index'
import Heart from '../Icon/icons/Heart/index'
import HeartFill from '../Icon/icons/HeartFill/index'
import ThumbUpOutline from '../Icon/icons/ThumbUpOutline/index'
import ThumbDownOutline from '../Icon/icons/ThumbDownOutline/index'

function getIconSize (size) {
  switch (size) {
    case VOTE_SIZE_LARGE:
      return 48
    case VOTE_SIZE_MEDIUM:
      return 36
    default:
      return 24
  }
}

function VideoPlayerVote (props) {
  const {
    setEventDefault,
    ga4EventData,
    thumbDownText,
    thumbUpText,
    heartText,
    contentId,
    contentType = TYPE_CONTENT_VIDEO,
    size,
    auth,
    userLanguage,
  } = props

  const authJwt = auth.get('jwt')
  const iconSize = getIconSize(size)
  const isLoggedIn = getAuthIsLoggedIn(auth)

  const [userVote, setUserVote] = useState(null)

  const {
    data: userVoteData,
    refetch: refetchVoteData,
  } = useQuery(GET_USER_VOTE, {
    variables: {
      contentId,
      contentType,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: auth.get('jwt'),
    },
  })

  const handleVote = async (direction, ga4Rating) => {
    if (!isLoggedIn || userVote === direction) {
      return
    }

    setUserVote(direction)

    await recommend({
      id: contentId,
      auth: authJwt,
      vote: direction,
      language: userLanguage,
    })

    refetchVoteData()

    // GA4 EVENT
    if (ga4EventData.size > 0) {
      setEventDefault(CONTENT_RATING_GA4_EVENT
        .mergeDeep(ga4EventData)
        .mergeDeep({ content_rating: ga4Rating }))
    }
  }

  useEffect(() => {
    const vote = _get(userVoteData, ['contentList', 'content', 0, 'vote', 'voteDirection'])
    setUserVote(vote ? VOTE_DIRECTIONS[vote] : null)
  }, [userVoteData])

  return (
    <div className={`video-player-vote video-player-vote--${size}`}>
      <Button
        type={TYPES.GHOST}
        onClick={() => handleVote(VOTE_DIRECTION_DOWN, 'down')}
        className={`video-player-vote__button--down ${userVote === VOTE_DIRECTION_DOWN ? 'button--active' : ''}`}
      >
        <ThumbDownOutline size={iconSize} className="video-player-vote__button-icon" />
        {!!thumbDownText && <p className="video-player-vote__button-text">{thumbDownText}</p>}
      </Button>
      <Button
        type={TYPES.GHOST}
        onClick={() => handleVote(VOTE_DIRECTION_UP, 'up')}
        className={`video-player-vote__button--up ${userVote === VOTE_DIRECTION_UP ? 'button--active' : ''}`}
      >
        <ThumbUpOutline size={iconSize} className="video-player-vote__button-icon" />
        {!!thumbUpText && <p className="video-player-vote__button-text">{thumbUpText}</p>}
      </Button>
      <Button
        type={TYPES.GHOST}
        onClick={() => handleVote(VOTE_DIRECTION_LOVE, 'love')}
        className={`video-player-vote__button--heart ${userVote === VOTE_DIRECTION_LOVE ? 'button--active' : ''}`}
      >
        {userVote === VOTE_DIRECTION_LOVE
          ? <HeartFill size={iconSize} className="video-player-vote__button-icon" />
          : <Heart size={iconSize} className="video-player-vote__button-icon" />
        }
        {!!heartText && <p className="video-player-vote__button-text">{heartText}</p>}
      </Button>
    </div>
  )
}

VideoPlayerVote.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  contentId: PropTypes.number.isRequired,
  contentType: PropTypes.oneOf([
    TYPE_CONTENT_VIDEO,
    TYPE_CONTENT_SERIES,
  ]),
  size: PropTypes.oneOf([
    VOTE_SIZE_SMALL,
    VOTE_SIZE_MEDIUM,
    VOTE_SIZE_LARGE,
  ]),
  heartText: PropTypes.string,
  thumbUpText: PropTypes.string,
  thumbDownText: PropTypes.string,
  ga4EventData: ImmutablePropTypes.map,
}

VideoPlayerVote.defaultProps = {
  size: VOTE_SIZE_SMALL,
  ga4EventData: Map(),
}

export default connect(
  (state) => ({
    auth: state.auth,
    userLanguage: getPrimary(state.user.getIn(['data', 'language'])),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setEventDefault: actions.eventTracking.setEventDefault,
    }
  },
)(VideoPlayerVote)
